'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Info, X, ChevronRight } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { getUnreadAlertCount, getUnreadMessageCount } from '@/lib/demoData';

const LEGAL_ITEMS = [
  {
    id: 'terms',
    label: "Conditions d\'utilisation",
    content: `LUCAT CONNECT FAMILLE est un service de communication dédié au lien entre une personne âgée et ses proches.\n\nEn utilisant ce service, vous acceptez de l'utiliser dans un cadre familial bienveillant et de respecter la vie privée d'André.\n\nL'accès est réservé aux proches autorisés par l'administrateur du compte.\n\nToute utilisation abusive ou non conforme pourra entraîner la suspension du compte.`,
  },
  {
    id: 'privacy',
    label: 'Politique de confidentialité',
    content: `LUCAT CONNECT collecte uniquement les données nécessaires au fonctionnement du service.\n\nVos données ne sont jamais vendues à des tiers. Elles sont hébergées de manière sécurisée et accessibles uniquement aux membres autorisés.\n\nVous disposez d'un droit d'accès, de rectification et de suppression de vos données personnelles.`,
  },
  {
    id: 'legal',
    label: 'Mentions légales',
    content: `LUCAT CONNECT FAMILLE — Prototype V1\n\nEnvironnement : Démonstration\n\nCe prototype est fourni à titre de démonstration uniquement. Les données présentées sont fictives.\n\nContenus juridiques provisoires — prototype de démonstration.`,
  },
  {
    id: 'licenses',
    label: 'Licences logicielles',
    content: `Ce prototype utilise des composants open source :\n\n• React — Licence MIT\n• Next.js — Licence MIT\n• Tailwind CSS — Licence MIT\n• Lucide Icons — Licence ISC\n\nLes textes et images de démonstration sont utilisés à des fins de prototype uniquement.`,
  },
];

export default function AboutClient() {
  const router = useRouter();
  const unreadAlerts = getUnreadAlertCount();
  const unreadMessages = getUnreadMessageCount();

  const [modal, setModal] = useState<string | null>(null);

  const activeItem = LEGAL_ITEMS?.find(i => i?.id === modal);

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      {/* Sub-header */}
      <div className="bg-lucat-navy px-4 pt-4 pb-5">
        <button
          onClick={() => router?.push('/settings')}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-medium mb-4 transition-colors"
          style={{ minHeight: 44 }}
          aria-label="Retour aux paramètres"
        >
          <ArrowLeft size={18} />
          Retour
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Info size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">À propos de LUCAT CONNECT</h1>
            <p className="text-sm text-white/70 mt-0.5">Informations sur l'application</p>
          </div>
        </div>
      </div>
      <div className="px-4 py-5 space-y-4 pb-8">
        {/* App identity card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col items-center text-center">
          {/* Logo officiel complet LUCAT CONNECT sur fond noir */}
          <div
            className="rounded-2xl overflow-hidden mb-5 flex items-center justify-center"
            style={{
              background: '#000000',
              padding: '16px',
              width: '100%',
              maxWidth: '280px',
            }}
          >
            <img
              src="/assets/images/03_LOGO_REFERENCE_LUCAT_CONNECT.jpg-1785216818095.jpeg"
              alt="LUCAT CONNECT Famille"
              style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </div>
          <h2 className="text-base font-bold text-lucat-navy">LUCAT CONNECT FAMILLE</h2>
          <div className="mt-3 space-y-1.5 w-full">
            {[
              { label: 'Version', value: 'Prototype V1' },
              { label: 'Numéro de version', value: '1.0.0' },
              { label: 'Environnement', value: 'Démonstration' },
            ]?.map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5">
                <span className="text-xs text-gray-500 font-medium">{label}</span>
                <span className="text-xs font-bold text-lucat-navy">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-sm text-gray-600 leading-relaxed text-center">
            LUCAT CONNECT facilite le lien entre une personne âgée et ses proches grâce à une interface simple, rassurante et accessible.
          </p>
        </div>

        {/* Legal entries */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {LEGAL_ITEMS?.map((item, idx) => (
            <button
              key={item?.id}
              onClick={() => setModal(item?.id)}
              className={`w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left ${idx < LEGAL_ITEMS?.length - 1 ? 'border-b border-gray-100' : ''}`}
              style={{ minHeight: 52 }}
              aria-label={`Consulter ${item?.label}`}
            >
              <span className="text-sm font-semibold text-gray-800">{item?.label}</span>
              <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center space-y-1 pt-2">
          <p className="text-xs text-gray-500 font-medium">© 2026 LUCAT CONNECT</p>
          <p className="text-xs text-orange-500">Contenus juridiques provisoires — prototype de démonstration</p>
        </div>
      </div>
      {/* Modal */}
      {modal && activeItem && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
          onClick={() => setModal(null)}
        >
          <div
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md mx-0 sm:mx-4 max-h-[80vh] overflow-y-auto"
            onClick={e => e?.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-lucat-navy">{activeItem?.label}</h3>
                <button onClick={() => setModal(null)} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fermer">
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <p className="text-xs text-orange-600 font-semibold bg-orange-50 rounded-lg px-3 py-2 mb-4">
                Contenu provisoire — prototype de démonstration
              </p>
              <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {activeItem?.content}
              </div>
              <button
                onClick={() => setModal(null)}
                className="w-full mt-5 bg-lucat-navy text-white font-bold text-sm rounded-2xl py-4 hover:bg-lucat-navy/90 active:scale-95 transition-all"
                aria-label="Fermer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
